import { isEqual } from "lodash";
import { ReactNode, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import {
  AnalysisMethod,
  AnalysisSet,
  ConditionComparatorEnum,
  DataSubset,
  GroupingFactor,
} from "../ars_ldm";
import { ARSContext, arsContextInitialState } from "./ARSContext";
import { arsContextReducer } from "./ARSContextReducer";
import {
  AnnotationVariable,
  ARS_CHANGE_SENT,
  ARSContextState,
  ARSDataState,
  IARSContext,
  LOAD_ANALYSIS_METHODS,
  LOAD_ANALYSIS_SETS,
  LOAD_DATA_SUBSETS,
  LOAD_GROUPING_FACTORS,
  LOAD_REFERENCE_DATA,
  REDO_ARS_CHANGE,
  UNDO_ARS_CHANGE,
} from "./interface";

/**
 * Props for the ARSContextProvider component.
 */
export interface ARSContextProviderProps {
  /** Child components to render within the provider */
  children: ReactNode;
  analysisMethods: AnalysisMethod[];
  analysisSets: AnalysisSet[];
  comparators: ConditionComparatorEnum[];
  dataSubsets: DataSubset[];
  groupingFactors: GroupingFactor[];
  updateARS: (state: ARSContextState) => void;
  variables: AnnotationVariable[];
}

/**
 * Provides ARS (Analysis Results Standard) context to child components.
 * Manages state for analysis sets, data subsets, grouping factors,
 * and related reference data.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 * @param params.analysisMethods - Initial list of analysis methods
 * @param params.analysisSets - Initial list of analysis sets
 * @param params.comparators - Initial list of condition comparators
 * @param params.dataSubsets - Initial list of data subsets
 * @param params.groupingFactors - Initial list of grouping factors
 * @param params.updateARS - Function to call when ARS state changes
 * @param params.variables - Initial list of annotation variables
 * @returns The ARSContextProvider component wrapping its children with context
 */
export const ARSContextProvider = ({
  children,
  analysisMethods,
  analysisSets,
  comparators,
  dataSubsets,
  groupingFactors,
  updateARS,
  variables,
}: ARSContextProviderProps): ReactNode => {
  const divRef = useRef<HTMLDivElement>(null);

  const initialDataState: ARSDataState = {
    analysisMethods: analysisMethods.reduce((acc, method) => ({ ...acc, [method.id]: method }), {}),
    analysisSets: analysisSets.reduce((acc, set) => ({ ...acc, [set.id]: set }), {}),
    comparators,
    dataSubsets: dataSubsets.reduce((acc, subset) => ({ ...acc, [subset.id]: subset }), {}),
    groupingFactors: groupingFactors.reduce((acc, factor) => ({ ...acc, [factor.id]: factor }), {}),
    variables,
    uiRequestStatus: { requesting: false, error: false },
    changed: false,
  };

  const [state, dispatch] = useReducer(arsContextReducer, {
    ...initialDataState,
    patches: [],
    ixPatch: -1,
  });

  // Keyboard event handler for undo/redo
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check if Ctrl key is pressed (or Cmd on Mac)
      /* istanbul ignore else */
      if (event.ctrlKey || event.metaKey) {
        /* istanbul ignore else */
        if (event.key.toLowerCase() === "z") {
          // Prevent default browser undo
          event.preventDefault();
          dispatch({ operation: UNDO_ARS_CHANGE });
        } else if (event.key.toLowerCase() === "y") {
          // Prevent default browser redo
          event.preventDefault();
          dispatch({ operation: REDO_ARS_CHANGE });
        }
      }
    },
    [dispatch],
  );

  // Add keyboard event listener to the div
  useEffect(() => {
    const div = divRef.current;
    /* istanbul ignore else */
    if (div) {
      div.addEventListener("keydown", handleKeyDown);
      return () => {
        div.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [handleKeyDown]);

  useEffect(() => {
    const eq = isEqual(state, arsContextInitialState);
    // Send changes up
    /* istanbul ignore else */
    if (state.changed && !eq) {
      updateARS(state);
      dispatch({ operation: ARS_CHANGE_SENT });
    } else if (!state.changed && !eq) {
      if (!isEqual(analysisMethods, Object.values(state.analysisMethods))) {
        dispatch({ operation: LOAD_ANALYSIS_METHODS, analysisMethods });
      }
      if (!isEqual(analysisSets, Object.values(state.analysisSets))) {
        dispatch({ operation: LOAD_ANALYSIS_SETS, analysisSets });
      }
      if (!isEqual(dataSubsets, Object.values(state.dataSubsets))) {
        dispatch({ operation: LOAD_DATA_SUBSETS, dataSubsets });
      }
      if (!isEqual(groupingFactors, Object.values(state.groupingFactors))) {
        dispatch({ operation: LOAD_GROUPING_FACTORS, groupingFactors });
      }
      if (!isEqual(variables, state.variables) || !isEqual(comparators, state.comparators)) {
        dispatch({ operation: LOAD_REFERENCE_DATA, variables, comparators });
      }
    }
  }, [
    analysisMethods,
    analysisSets,
    comparators,
    dataSubsets,
    groupingFactors,
    state,
    updateARS,
    variables,
  ]);

  return (
    <ARSContext.Provider value={{ state, dispatch }}>
      <div
        ref={divRef}
        tabIndex={-1}
        style={{ outline: "none", width: "100%", height: "100%" }}
      >
        {children}
      </div>
    </ARSContext.Provider>
  );
};

ARSContextProvider.displayName = "ARSContextProvider";

/**
 * Hook to access the ARS context.
 *
 * @returns The ARS context value containing state and dispatch
 */
export const useARSContext = (): IARSContext => {
  const context = useContext(ARSContext);
  return context;
};
